import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import { ProjectGuard } from '../components/common/ProjectGuard';
import { useProjectStore } from '../stores/project.store';
import { useProject } from '../api/queries/projects';

export function StoryStudioListRoute() {
  const { activeProjectId } = useProjectStore();

  return (
    <ProjectGuard>
      {activeProjectId && <StoryStudioContent projectId={activeProjectId} />}
    </ProjectGuard>
  );
}

function StoryStudioContent({ projectId }: { projectId: string }) {
  const { data: project, isLoading, isError } = useProject(projectId);
  const navigate = useNavigate();

  const sessionId = project?.sessionId;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Story Studio{project ? ` \u2014 ${project.name}` : ''}
        </Typography>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load project.</Alert>}

      {sessionId && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <EditNoteIcon color="primary" sx={{ fontSize: 40 }} />
              <Stack sx={{ flex: 1 }}>
                <Typography variant="h6">Drafting Session</Typography>
                <Typography variant="body2" color="text.secondary">
                  AI-assisted drafting to brainstorm, outline, and write your
                  story.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigate({
                    to: '/story-studio/$sessionId',
                    params: { sessionId },
                  })
                }
              >
                Open Session
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!isLoading && !sessionId && (
        <EmptyState
          icon={<EditNoteIcon sx={{ fontSize: 64 }} />}
          title="No session available"
          description="A story studio session will be created when the project is scaffolded. Go to the project page to get started."
        />
      )}
    </Paper>
  );
}
